import { SBF_THRESHOLD } from 'small-batch-fee-constants';

/**
 * Whether the pre-purchase small-batch-fee notice should be visible.
 *
 * `chargedQty` is the cart-line quantity the fee is assessed on —
 * `qty × _sbf_sqft_per_unit`, i.e. the total *with* overage, NOT the raw
 * Square Feet input. Keying off the charged total (rather than the typed
 * value) keeps the PDP message in lockstep with the fee actually applied at
 * the cart (small-batch-fee-sync.js qualifies a line when `< 25`). A blank /
 * zero / non-positive quantity returns false so we never nag before the
 * shopper has entered an amount.
 *
 * @param {number} chargedQty
 * @returns {boolean}
 */
export function shouldShowSbfNotice(chargedQty) {
    return Number.isFinite(chargedQty) && chargedQty > 0 && chargedQty < SBF_THRESHOLD;
}

// <small-batch-fee-notice> toggles its own visibility. Two binding modes:
//   1. Inside a <sqft-calculator> (sqft / lf / box MTO): listens to the
//      calculator's `sqft:change` event and reads detail.sbfChargedQty.
//   2. data-quantity-input-id set (handpainted MTO — a plain quantity stepper,
//      no calculator): listens to that input. Piece count is the charged basis
//      (_sbf_sqft_per_unit is 1), so qty itself is compared to the threshold.
class SmallBatchFeeNotice extends HTMLElement {
    connectedCallback() {
        const qtyInputId = this.getAttribute('data-quantity-input-id');
        if (qtyInputId) {
            const input = document.getElementById(qtyInputId);
            if (!input) return;
            const update = () => {
                const raw = String(input.value).trim();
                const qty = parseInt(raw, 10);
                this._toggle(raw === '' ? 0 : qty);
            };
            this._input = input;
            this._updateHandler = update;
            input.addEventListener('input', update);
            input.addEventListener('change', update);
            update();
            return;
        }

        const calculator = this.closest('sqft-calculator');
        if (!calculator) return;
        const calculatorHandler = (event) => {
            this._toggle(event.detail && event.detail.sbfChargedQty);
        };
        this._calculator = calculator;
        this._calculatorHandler = calculatorHandler;
        calculator.addEventListener('sqft:change', calculatorHandler);
        // The calculator dispatches its first `sqft:change` from its own
        // connectedCallback. Depending on custom-element upgrade order that may
        // fire before this listener attaches, so seed the initial state from the
        // calculator's current value too.
        this._toggle(calculator.sbfChargedQty);
    }

    disconnectedCallback() {
        if (this._input && this._updateHandler) {
            this._input.removeEventListener('input', this._updateHandler);
            this._input.removeEventListener('change', this._updateHandler);
        }
        if (this._calculator && this._calculatorHandler) {
            this._calculator.removeEventListener('sqft:change', this._calculatorHandler);
        }
    }

    _toggle(chargedQty) {
        this.hidden = !shouldShowSbfNotice(chargedQty);
    }
}

if (!window.customElements.get('small-batch-fee-notice')) {
    window.customElements.define('small-batch-fee-notice', SmallBatchFeeNotice);
}
